import { useCallback, useEffect, useState } from "react";
import { api, mesajEroare } from "./api.js";
import { REGIM_PREVENTIV_TOATE, STARI_PRET_NEGOCIAT, eticheta } from "./enums.js";
import { data } from "./text.js";
import { tr } from "./i18n.js";

const PRETURI = ["ADMIN_TENANT", "MANAGER_TEHNIC", "FINANCIAR"];

const bani = (n) => Number(n ?? 0).toLocaleString("ro-RO", { minimumFractionDigits: 2 });

/**
 * Ce promite contractul despre preventiv, față de ce e planificat efectiv.
 *
 * Până acum cifra de cicluri se completa în formular și n-o citea nimeni. Aici
 * se vede diferența — fără să blocheze ceva: un plan per sistem instalat poate
 * genera legitim mai multe vizite decât promite contractul, dacă se grupează.
 */
export function RegimPreventiv({ client }) {
  const [linii, setLinii] = useState(null);
  const [eroare, setEroare] = useState("");

  const reincarca = useCallback(async () => {
    try { setLinii(await api.regimPreventiv(client.id)); setEroare(""); }
    catch (e) { setEroare(mesajEroare(e)); setLinii([]); }
  }, [client.id]);

  useEffect(() => { reincarca(); }, [reincarca]);

  return (
    <div className="panou">
      <div className="panou-antet">
        <h2>{tr("Mentenanță preventivă — ce s-a semnat")}</h2>
        <span className="numar">{linii?.length ?? 0}</span>
      </div>
      <div className="panou-corp">
        {eroare && <div className="eroare">{eroare}</div>}
        {linii === null ? (
          <div className="gol">{tr("Se încarcă…")}</div>
        ) : linii.length === 0 ? (
          <div className="gol">
            {tr("Niciun contract activ. Regimul de preventiv se declară pe contract, fiindcă e o clauză comercială — un client cu două obiective poate avea preventiv la unul și nimic la celălalt.")}
          </div>
        ) : linii.map((l) => (
          <div key={l.contract.id} className="regim-linie">
            <div className="contor-antet">
              <strong>{eticheta(REGIM_PREVENTIV_TOATE, l.regim)}</strong>
              <span className="cod-mic">
                {l.contract.numar} · {data(l.contract.valabilDeLa)} → {data(l.contract.valabilPanaLa)}
              </span>
            </div>

            <div className="contor-cifre">
              {l.cicluriPromisePeAn !== null && (
                <span><strong>{l.cicluriPromisePeAn}</strong> {tr("cicluri promise pe an")}</span>
              )}
              <span><strong>{l.cicluriPlanificatePeAn}</strong> {tr("planificate efectiv")}</span>
              {l.planuri.length > 0 && (
                <span className="cod-mic">
                  {l.planuri.map((p) => `${p.sistem.denumire} (${p.cicluriPeAn}/an)`).join(", ")}
                </span>
              )}
            </div>

            {l.asumare && (
              <p className="subtitlu">
                <strong>{tr("Asumat de client:")}</strong> „{l.asumare.motiv}{tr("\" — consemnat de")}{" "}
                {l.asumare.consemnatDe?.numeComplet ?? tr("necunoscut")}, {data(l.asumare.la)}.
              </p>
            )}

            {l.verdict && (
              <div className="panou panou-atentie"><div className="panou-corp">{l.verdict}</div></div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Prețurile negociate cu acest client. Tariful orar intră tot aici: manopera e
 * un articol de catalog, deci un tarif diferit e o linie pe acel articol — n-a
 * cerut niciun câmp nou.
 */
export function PreturiNegociate({ eu, client }) {
  const [linii, setLinii] = useState(null);
  const [toate, setToate] = useState(false);
  const [eroare, setEroare] = useState("");
  const [formular, setFormular] = useState(false);
  const [respingCu, setRespingCu] = useState(null);
  const [motiv, setMotiv] = useState("");
  const [lucreaza, setLucreaza] = useState(false);
  const poate = PRETURI.includes(eu.rol);
  /*
   * Dreptul de aprobare se ÎNTREABĂ, nu se deduce dintr-o listă de roluri:
   * firma îl poate muta pe oricare funcție, iar butonul trebuie să se mute
   * odată cu el. `null` = „încă nu știu", și NU e „am voie".
   */
  const [poateAproba, setPoateAproba] = useState(null);

  useEffect(() => {
    api.drepturileMele()
      .then((d) => setPoateAproba((d?.catalog ?? []).includes("aprobare")))
      .catch(() => setPoateAproba(false));
  }, []);

  const reincarca = useCallback(async () => {
    try { setLinii(await api.preturiClient(client.id, toate)); setEroare(""); }
    catch (e) { setEroare(mesajEroare(e)); setLinii([]); }
  }, [client.id, toate]);

  useEffect(() => { reincarca(); }, [reincarca]);

  /*
   * `tabel-derulabil`: coloana de stare a lățit tabelul peste panou, iar
   * `.panou` taie ce iese (`overflow: hidden`). Pe desktop dispăreau chiar
   * butoanele de decizie — o poartă la care nu se poate ajunge nu e o poartă.
   */
  return (
    <div className="panou tabel-derulabil">
      <div className="panou-antet">
        <h2>{tr("Prețuri negociate")}</h2>
        <span className="numar">{linii?.length ?? 0}</span>
        {poate && <button className="buton-mic" onClick={() => setFormular(true)}>{tr("adaugă preț")}</button>}
      </div>
      {poateAproba === true && <PragDiscount />}
      <div className="panou-corp">
        {eroare && <div className="eroare">{eroare}</div>}

        <label className="comutator-dezactivate">
          <input type="checkbox" checked={toate} onChange={(e) => setToate(e.target.checked)} />
          {tr("arată și prețurile încheiate")}
        </label>

        {linii === null ? (
          <div className="gol">{tr("Se încarcă…")}</div>
        ) : linii.length === 0 ? (
          <div className="gol">
            {tr("Niciun preț negociat. Clientul plătește lista de catalog — inclusiv tariful orar. Un tarif diferit se pune aici, pe articolul de manoperă.")}
          </div>
        ) : (
          <table className="tabel">
            <thead>
              <tr><th>{tr("Articol")}</th><th>{tr("Preț negociat")}</th><th>{tr("Catalog")}</th>
                <th>{tr("Stare")}</th><th>{tr("Valabil")}</th>
                {poate && <th></th>}</tr>
            </thead>
            <tbody>
              {linii.map((l) => (
                <tr key={l.id} className={l.valabilPanaLa ? "tip-dezactivat" : ""}>
                  <td>
                    {tr(l.articol.denumire)}
                    <span className="cod-mic">{l.articol.cod} · {l.articol.tip}</span>
                  </td>
                  <td className="num">
                    {l.pretVanzare !== null
                      ? `${bani(l.pretVanzare)} / ${l.articol.unitateMasura}`
                      : tr("−{0}% față de catalog", bani(l.discountProcent))}
                  </td>
                  <td className="num">{bani(l.articol.pretVanzare)}</td>
                  <td className="celula-stare">
                    {eticheta(STARI_PRET_NEGOCIAT, l.stare)}
                    {/* Doar o reducere ADEVĂRATĂ. Un preț negociat mai mare
                        decât catalogul dădea „reducere −25%", care se citește
                        de două ori până să însemne ceva; cifrele de alături
                        spun deja 250 față de 200. */}
                    {Number(l.procentEfectiv) > 0 && (
                      <span className="cod-mic">{tr("reducere")} {bani(l.procentEfectiv)}%</span>
                    )}
                    {l.stare === "Respins" && l.motivRespingere && (
                      <span className="cod-mic">{l.motivRespingere}</span>
                    )}
                  </td>
                  <td>{data(l.valabilDeLa)} → {l.valabilPanaLa ? data(l.valabilPanaLa) : tr("în vigoare")}</td>
                  {poate && (
                    <td className="celula-actiuni">
                      {/* Un rând în așteptare nu se încheie: nu e în vigoare
                          nimic de încheiat. Se aprobă sau se respinge. */}
                      {l.stare === "AsteaptaAprobare" ? (
                        poateAproba === true ? (
                          <>
                            <button className="buton-mic" disabled={lucreaza} onClick={async () => {
                              setLucreaza(true); setEroare("");
                              try { await api.aprobaPretClient(l.id); await reincarca(); }
                              catch (e) { setEroare(mesajEroare(e)); }
                              finally { setLucreaza(false); }
                            }}>{tr("aprobă")}</button>
                            <button className="buton-mic" disabled={lucreaza}
                              onClick={() => { setRespingCu(l.id); setMotiv(""); }}>
                              {tr("respinge")}
                            </button>
                          </>
                        ) : (
                          <span className="cod-mic">{tr("la aprobat")}</span>
                        )
                      ) : (
                        !l.valabilPanaLa && l.stare === "Activ" && (
                          <button className="buton-mic" onClick={async () => {
                            try { await api.incheiePretClient(l.id); await reincarca(); }
                            catch (e) { setEroare(mesajEroare(e)); }
                          }}>{tr("încheie")}</button>
                        )
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {formular && (
        <FormPret clientId={client.id} inchide={() => setFormular(false)}
          gata={async () => { setFormular(false); await reincarca(); }} />
      )}

      {respingCu && (
        <div className="modal-fundal" onClick={() => setRespingCu(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>{tr("Respingi prețul negociat")}</h3>
            <p className="subtitlu">
              {tr("Prețul de dinainte rămâne în vigoare. Cel care l-a cerut e anunțat cu motivul.")}
            </p>
            <div className="camp">
              <label htmlFor="p-motiv">{tr("De ce")}</label>
              <textarea id="p-motiv" rows={2} value={motiv}
                placeholder={tr("ex. prea mult pentru volumul lui")}
                onChange={(e) => setMotiv(e.target.value)} />
            </div>
            <div className="modal-actiuni">
              <button className="buton buton-sters" onClick={() => setRespingCu(null)}>{tr("renunță")}</button>
              <button className="buton buton-actiune"
                disabled={lucreaza || motiv.trim().length < 5}
                onClick={async () => {
                  setLucreaza(true); setEroare("");
                  try {
                    await api.respingePretClient(respingCu, motiv.trim());
                    setRespingCu(null); setMotiv(""); await reincarca();
                  } catch (e) { setEroare(mesajEroare(e)); }
                  finally { setLucreaza(false); }
                }}>
                {tr("trimite respingerea")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Pragul de reducere al firmei.
 *
 * Îl așază cine poate APROBA, nu cine pune prețuri: altfel cel care negociază
 * reducerea ar putea ridica singur bara peste care nu mai trece nimic.
 */
function PragDiscount() {
  const [stare, setStare] = useState(null);
  const [valoare, setValoare] = useState("");
  const [deschis, setDeschis] = useState(false);
  const [lucreaza, setLucreaza] = useState(false);
  const [eroare, setEroare] = useState("");

  const citeste = useCallback(async () => {
    try {
      const r = await api.pragDiscount();
      setStare(r);
      setValoare(r.prag === null ? "" : String(r.prag));
    } catch (e) { setEroare(mesajEroare(e)); }
  }, []);
  useEffect(() => { citeste(); }, [citeste]);

  async function salveaza(prag) {
    setLucreaza(true); setEroare("");
    try { await api.setPragDiscount(prag); await citeste(); setDeschis(false); }
    catch (e) { setEroare(mesajEroare(e)); }
    finally { setLucreaza(false); }
  }

  if (!stare) return null;

  return (
    <div className="panou-corp" style={{ paddingBottom: 0 }}>
      {eroare && <div className="eroare">{eroare}</div>}
      <p className="nota-cautare" style={{ marginTop: 0 }}>
        {stare.activ
          ? tr("Reducerile peste {0}% nu intră în vigoare până nu le aprobă cineva cu dreptul. Până atunci se aplică prețul de dinainte.", stare.prag)
          : tr("Nu e pus niciun prag de reducere: prețurile negociate se aplică direct.")}
        {" "}
        <button className="buton-mic" onClick={() => setDeschis(!deschis)}>
          {deschis ? tr("închide") : tr("schimbă")}
        </button>
      </p>
      {deschis && (
        <div className="linie-actiune" style={{ alignItems: "center" }}>
          <label>{tr("Peste reducerea de (%)")}
            <input className="camp" type="number" min={0} max={100} step="0.01" value={valoare}
              style={{ minWidth: "7rem", width: "7rem" }}
              onChange={(e) => setValoare(e.target.value)} />
          </label>
          <button className="buton buton-mic buton-actiune"
            disabled={lucreaza || valoare === ""}
            onClick={() => salveaza(Number(valoare))}>
            {tr("pune pragul")}
          </button>
          {stare.activ && (
            <button className="buton buton-mic buton-sters" disabled={lucreaza}
              onClick={() => salveaza(null)}>
              {tr("scoate pragul")}
            </button>
          )}
        </div>
      )}
      {deschis && (
        <p className="nota">
          {tr("Poarta se uită la reducerea REALĂ față de catalog, oricum ar fi scrisă: un preț fix mic e tot o reducere, deci trece pe la aprobare la fel ca un procent.")}
        </p>
      )}
    </div>
  );
}

function FormPret({ clientId, inchide, gata }) {
  const [articole, setArticole] = useState([]);
  const [f, setF] = useState({ articolId: "", fel: "pret", valoare: "" });
  const [eroare, setEroare] = useState("");
  const [lucreaza, setLucreaza] = useState(false);

  useEffect(() => {
    api.articoleLista().then(setArticole).catch((e) => setEroare(mesajEroare(e)));
  }, []);

  const lipsesc = [!f.articolId && "articolul", !f.valoare && "valoarea"].filter(Boolean);

  async function salveaza() {
    setLucreaza(true); setEroare("");
    try {
      await api.aseazaPretClient({
        clientId, articolId: f.articolId,
        // Ori preț fix, ori discount — niciodată amândouă, ca să nu se pună
        // întrebarea „care câștigă". Serverul refuză oricum, dar formularul
        // nici măcar nu lasă să se ajungă acolo.
        ...(f.fel === "pret"
          ? { pretVanzare: Number(f.valoare) }
          : { discountProcent: Number(f.valoare) }),
      });
      await gata();
    } catch (e) { setEroare(mesajEroare(e)); }
    finally { setLucreaza(false); }
  }

  return (
    <div className="modal-fundal" onClick={inchide}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>{tr("Preț negociat cu clientul")}</h3>
        {eroare && <div className="eroare">{eroare}</div>}

        <label className="camp">
          {tr("articolul (manopera e tot aici — tariful orar e un articol)")}
          <select value={f.articolId}
            onChange={(e) => setF({ ...f, articolId: e.target.value })}>
            <option value="">{tr("— alege —")}</option>
            {articole.map((a) => (
              <option key={a.id} value={a.id}>{a.cod} · {tr(a.denumire)}</option>
            ))}
          </select>
        </label>

        <label className="camp">
          {tr("cum se negociază")}
          <select value={f.fel}
            onChange={(e) => setF({ ...f, fel: e.target.value })}>
            <option value="pret">{tr("preț fix")}</option>
            <option value="discount">{tr("discount față de catalog (%)")}</option>
          </select>
        </label>

        <label className="camp">
          {f.fel === "pret" ? tr("prețul negociat") : tr("discountul, în procente")}
          <input type="number" min="0" step="0.01" value={f.valoare}
            onChange={(e) => setF({ ...f, valoare: e.target.value })} />
        </label>

        {lipsesc.length > 0 && <p className="subtitlu">{tr("Completează:")} {lipsesc.join(", ")}.</p>}

        <div className="modal-actiuni">
          <button className="buton buton-sters" onClick={inchide}>{tr("renunță")}</button>
          <button className="buton" disabled={lucreaza || lipsesc.length > 0}
            onClick={salveaza}>{tr("salvează")}</button>
        </div>
      </div>
    </div>
  );
}
